import {useEffect} from 'react'

// How many layers currently hold each scroller, and the inline overflow it
// had before the first of them took it. Shared so that two layers open at
// once — the phone refine sheet handing over to the Enrich takeover in the
// same update, a modal over the takeover — cannot each save and restore the
// same style: the first lock records the original, the last release puts it
// back, and the page stays locked for as long as any layer is up.
const holds = new Map()

/**
 * Locks the given scrollers (`overflow: hidden`) and returns a function that
 * releases this hold. The original inline overflow returns only when the
 * last hold on an element is released.
 */
export const acquireScrollLock = scrollers => {
  scrollers.forEach(element => {
    const hold = holds.get(element)

    if (hold) {
      hold.count += 1
    } else {
      holds.set(element, {count: 1, overflow: element.style.overflow})
      element.style.overflow = 'hidden'
    }
  })

  let released = false

  return () => {
    if (released) return

    released = true
    scrollers.forEach(element => {
      const hold = holds.get(element)

      if (!hold) return

      hold.count -= 1

      if (hold.count === 0) {
        holds.delete(element)
        element.style.overflow = hold.overflow
      }
    })
  }
}

/** The page's scrollers behind a layer: the body, and the flow's own scroll container it sits in. */
export const scrollersBehind = element => {
  const flow = element && element.closest('.life-story-flow')

  return [document.body, ...flow ? [flow] : []]
}

/**
 * Keeps the page behind a fixed layer (the Enrich takeover, the Record this
 * version modal) from scrolling while it is open: the body, and the flow's
 * own scroll container (`.life-story-flow` is `overflow-y: auto`), which a
 * touch outside the layer or scroll chaining from its edge would otherwise
 * still move. Each element's previous inline overflow is put back once the
 * last layer holding it closes.
 *
 * `ref` is the layer itself, used to find the flow container it sits in.
 */
const useScrollLock = ref => {
  useEffect(() => acquireScrollLock(scrollersBehind(ref.current)), [])
}

export default useScrollLock
